import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { AlertNotification, Csku, Loading, Text } from "@commonsku/styles";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { toast } from "react-toastify";
import ReportFiltersContainer from "../report/filters/ReportFiltersContainer";
import SummaryItem from "../report/summary/SummaryItem";
import { mapSummaryDataByConfig } from "../report/summary/types";
import { useReportContext } from "../report/report_context";
import StaticReportTable, {
  StaticReportRow,
} from "../report/table/StaticReportTable";
import BulkActionBar from "../BulkActionBar";
import { oauth } from "../../utils";
import { PodBillsRefreshContext } from "./PodBillsRefreshContext";

/**
 * ReportProvider cannot fetch this itself: the route answers with a bare array instead of the
 * `{ report, totals }` envelope, and takes no parameters — it is scoped to the authenticated
 * tenant's unbilled lines. Which is also why the filters below are applied here.
 */
interface PodBill {
  pod_invoice_order_id: string;
  pod_invoice_id: string;
  date_invoiced: string | null;
  order_id: string;
  customer_po: string | null;
  date_orderdate: string | null;
  supplier_order_id: string | null;
  item_cost: number;
  pick_and_pack: number;
  shipping: number;
  total: number;
  currency_id: string | null;
  client_id: string | null;
  client_name: string | null;
  shop_id: string | null;
  shop_number: string | null;
  shop_name: string | null;
  division_id: string | null;
  division_name: string | null;
  supplier_id: string | null;
}

const DAY_FORMAT = "yyyy-MM-dd";

const day = (value: unknown) => String(value ?? "").slice(0, 10);

/**
 * Resolves a date preset into the days it covers.
 *
 * The picker sends a preset by name only, leaving whoever holds the rows to turn it into dates.
 * That is normally the report endpoint; this one filters nothing, so it happens here.
 */
function presetRange(category: string): [Date, Date] | null {
  const today = new Date();

  switch (category) {
    case "today":
      return [today, today];
    case "yesterday": {
      const date = subDays(today, 1);
      return [date, date];
    }
    case "this_week":
      return [startOfWeek(today), endOfWeek(today)];
    case "last_week": {
      const date = subWeeks(today, 1);
      return [startOfWeek(date), endOfWeek(date)];
    }
    case "this_month":
      return [startOfMonth(today), endOfMonth(today)];
    case "last_month": {
      const date = subMonths(today, 1);
      return [startOfMonth(date), endOfMonth(date)];
    }
    case "this_quarter":
      return [startOfQuarter(today), endOfQuarter(today)];
    case "previous_quarter": {
      const date = subQuarters(today, 1);
      return [startOfQuarter(date), endOfQuarter(date)];
    }
    case "this_year":
      return [startOfYear(today), endOfYear(today)];
    case "last_year": {
      const date = subYears(today, 1);
      return [startOfYear(date), endOfYear(date)];
    }
    case "last_ytd": {
      const date = subYears(today, 1);
      return [startOfYear(date), date];
    }
    default:
      return null;
  }
}

function dateBounds(query: Record<string, unknown>): [string, string] {
  const start = day(query.start_stamp);
  const end = day(query.end_stamp);

  if (start !== "" || end !== "") {
    return [start, end];
  }

  const preset = presetRange(String(query.date_category ?? ""));

  return preset
    ? [format(preset[0], DAY_FORMAT), format(preset[1], DAY_FORMAT)]
    : ["", ""];
}

function filterBills(bills: PodBill[], query: Record<string, unknown>) {
  const search = String(query.search ?? "")
    .trim()
    .toLowerCase();
  const divisionId = String(query.division_id ?? "");
  const clientId = String(query.client_id ?? "");
  const [start, end] = dateBounds(query);

  return bills.filter((bill) => {
    const invoicedOn = day(bill.date_invoiced);
    const matchesSearch =
      search === "" ||
      [bill.shop_number, bill.shop_name, bill.client_name].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(search),
      );

    return (
      matchesSearch &&
      (divisionId === "" || bill.division_id === divisionId) &&
      (clientId === "" || bill.client_id === clientId) &&
      (start === "" || invoicedOn >= start) &&
      (end === "" || invoicedOn <= end)
    );
  });
}

const SummaryContainer = styled(Csku)`
  background-color: #edf4f7;
  padding: 10px 0;
  margin: 20px 8px !important;
  display: flex;
`;

const EmptyMessage = styled(Text)`
  display: block;
  padding: 24px 8px;
`;

const FinancePodBills = () => {
  const {
    reportConfig: { endpoint, summary, bulkActions },
    query,
  } = useReportContext();
  const [allBills, setAllBills] = useState<PodBill[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<StaticReportRow[]>([]);
  const abortController = useRef<AbortController>();

  const loadBills = useCallback(async () => {
    abortController.current?.abort();
    const controller = new AbortController();
    abortController.current = controller;

    setLoading(true);
    setFailed(false);

    try {
      const { json } = await oauth(
        "GET",
        endpoint,
        {},
        undefined,
        { signal: controller.signal },
      );
      if (controller.signal.aborted) {
        return;
      }
      setAllBills(Array.isArray(json) ? json : []);
      setLoading(false);
    } catch {
      if (controller.signal.aborted) {
        return;
      }
      setFailed(true);
      setLoading(false);
      toast.error("Unable to load Print on Demand bills");
    }
  }, [endpoint]);

  useEffect(() => {
    loadBills();

    return () => {
      abortController.current?.abort();
    };
  }, [loadBills]);

  const bills = useMemo(() => filterBills(allBills, query), [allBills, query]);

  // Matches what the provider does on every fetch: a selection cannot outlive the rows it was made
  // from, or the bulk bar counts rows that are no longer on screen.
  useEffect(() => setSelectedRows([]), [bills]);

  const summaryData = useMemo(() => {
    if (!summary) {
      return [];
    }
    const unbilled = bills.reduce((sum, bill) => sum + Number(bill.total), 0);

    return mapSummaryDataByConfig(
      { unbilled_amount: unbilled, row_count: bills.length },
      summary,
    );
  }, [summary, bills]);

  if (failed) {
    return (
      <AlertNotification alertType="error">
        Print on Demand bills could not be loaded. Please try again later.
      </AlertNotification>
    );
  }

  return (
    <PodBillsRefreshContext.Provider value={loadBills}>
      <ReportFiltersContainer onGetReport={loadBills} />
      {summaryData.length > 0 ? (
        <SummaryContainer>
          {summaryData.map((item) => (
            <SummaryItem
              key={item.label}
              data={item.data}
              type={item.type}
              label={item.label}
            />
          ))}
        </SummaryContainer>
      ) : null}
      {loading ? <Loading /> : null}
      {!loading && bills.length > 0 ? (
        <StaticReportTable
          rows={bills}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
        />
      ) : null}
      {!loading && bills.length === 0 ? (
        <EmptyMessage>
          {allBills.length === 0
            ? "There are no unbilled Print on Demand bills."
            : "No bills match these filters."}
        </EmptyMessage>
      ) : null}
      {bulkActions ? (
        <BulkActionBar
          selected={selectedRows}
          prepareLabel={bulkActions.prepareLabel}
          actions={bulkActions.actions}
        />
      ) : null}
    </PodBillsRefreshContext.Provider>
  );
};

export default FinancePodBills;
